import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  Typography, 
  Tag
} from 'antd';
import { 
  EnvironmentOutlined, 
  MailOutlined, 
  ReloadOutlined,
  SafetyOutlined,
  SecurityScanOutlined,
  LockOutlined
} from '@ant-design/icons';
import API from '../services/api';

const { Text } = Typography;

/**
 * 风险验证弹窗组件
 * 用于处理异地登录风险评估后的身份验证
 */
const RiskVerificationModal = ({
  visible,
  onClose,
  onVerificationSuccess,
  riskData,
  pendingLoginData,
  isMobile = false
}) => {
  const [form] = Form.useForm();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendCaptcha, setResendCaptcha] = useState({
    captchaId: '',
    captchaSvg: '',
    expiresIn: 0
  });

  // 重发验证码倒计时
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // 获取重发用的图形验证码
  const getResendCaptcha = async () => {
    try {
      const response = await API.get('/api/register/captcha');
      setResendCaptcha({
        captchaId: response.data.captchaId,
        captchaSvg: response.data.captchaSvg,
        expiresIn: response.data.expiresIn
      });
    } catch (error) {
      console.error('获取验证码失败:', error);
      Modal.error({
        title: '获取验证码失败',
        content: '获取图形验证码失败，请稍后再试'
      });
    }
  };

  // 弹窗打开时获取图形验证码
  useEffect(() => {
    if (visible) {
      getResendCaptcha();
      setResendCooldown(60); // 第一次发送验证码后立即开始倒计时
    }
  }, [visible]);

  // 获取风险等级文本和颜色
  const getRiskLevelConfig = (level) => {
    const configs = {
      'low': { text: '低风险', color: '#52c41a' },
      'medium': { text: '中等风险', color: '#faad14' },
      'high': { text: '高风险', color: '#ff7a45' },
      'critical': { text: '严重风险', color: '#ff4d4f' }
    };
    return configs[level] || { text: '未知风险', color: '#8c8c8c' };
  };

  // 处理验证码提交
  const handleVerificationSubmit = async (values) => {
    setIsVerifying(true);
    try {
      // 调用父组件的验证成功回调
      await onVerificationSuccess(values.verificationCode);
    } catch (error) {
      console.error('验证失败:', error);
      Modal.error({
        title: '验证失败',
        content: error.message || '验证失败，请重试'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  // 重新发送验证码
  const handleResendVerification = async (captchaCode) => {
    if (resendCooldown > 0 || !pendingLoginData?.username) return;

    if (!captchaCode) {
      Modal.error({
        title: '请输入图形验证码',
        content: '请先输入图形验证码再点击重新发送'
      });
      return;
    }

    setIsResending(true);
    try {
      const response = await API.post('/api/auth/resend-login-verification', {
        username: pendingLoginData.username,
        captchaId: resendCaptcha.captchaId,
        captchaCode: captchaCode
      });

      Modal.success({
        title: '验证码已重新发送',
        content: response.data.message || '验证码已发送到您的邮箱，请查收'
      });

      setResendCooldown(60); // 60秒冷却时间
      // 清除图形验证码字段并刷新验证码
      form.setFieldsValue({ resendCaptchaCode: '' });
      getResendCaptcha();
    } catch (err) {
      console.error('重发验证码失败:', err);
      let errorMessage = err.response?.data?.message || '重发验证码失败，请稍后再试';
      
      // 如果错误信息包含验证码相关内容，提供更明确的提示
      if (errorMessage.includes('验证码') || errorMessage.includes('captcha')) {
        errorMessage = '图形验证码错误，请重新输入';
      }
      
      Modal.error({
        title: '重发失败',
        content: errorMessage
      });
      
      // 刷新图形验证码并清除输入
      getResendCaptcha();
      form.setFieldsValue({ resendCaptchaCode: '' });
    } finally {
      setIsResending(false);
    }
  };

  // 关闭弹窗
  const handleClose = () => {
    form.resetFields();
    setResendCooldown(0);
    onClose();
  };

  const riskLevelConfig = riskData?.riskAssessment ? 
    getRiskLevelConfig(riskData.riskAssessment.riskLevel) : 
    { text: '未知', color: '#8c8c8c' };

  return (
    <Modal
      title={
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <SecurityScanOutlined style={{ 
            fontSize: '20px',
            color: '#1890ff', 
            marginRight: '8px'
          }} />
          <span style={{ fontSize: '16px', fontWeight: 500 }}>
            异常登录验证
          </span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={isMobile ? '95%' : 480}
      destroyOnClose
      maskClosable={false}
      centered
    >

      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        {/* 风险提示 */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '20px',
          padding: '16px',
          background: '#fff2e8',
          borderRadius: '6px',
          border: '1px solid #ffbb96'
        }}>
          <LockOutlined style={{ 
            fontSize: '24px', 
            color: '#fa8c16',
            marginBottom: '8px'
          }} />
          <div style={{ 
            color: '#d4380d',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '4px'
          }}>
            检测到异常登录行为
          </div>
          <div style={{ 
            color: '#8c8c8c', 
            fontSize: '13px'
          }}>
            {riskData?.message || '验证码已发送到您的邮箱，请查收并输入验证码'}
          </div>
        </div>

        {/* 风险信息 */}
        {(riskData?.riskAssessment || riskData?.currentLocation || riskData?.lastLocation) && (
          <div style={{ 
            marginBottom: '20px',
            padding: '12px',
            background: '#f8f9fa',
            borderRadius: '6px',
            fontSize: '13px'
          }}>
            {riskData?.riskAssessment && (
              <div style={{ marginBottom: '8px' }}>
                <Text strong>风险等级：</Text>
                <Tag color={riskLevelConfig.color} size="small">
                  {riskLevelConfig.text} ({riskData.riskAssessment.riskScore}分)
                </Tag>
              </div>
            )}
            {riskData.currentLocation && (
              <div style={{ marginBottom: '4px' }}>
                <EnvironmentOutlined style={{ marginRight: '4px', color: '#8c8c8c' }} />
                <Text>当前位置：{riskData.currentLocation}</Text>
              </div>
            )}
            {riskData.lastLocation && (
              <div>
                <EnvironmentOutlined style={{ marginRight: '4px', color: '#8c8c8c' }} />
                <Text>上次位置：{riskData.lastLocation}</Text>
              </div>
            )}
          </div>
        )}

        {/* 验证表单 */}
        <Form
          form={form}
          onFinish={handleVerificationSubmit}
          layout="vertical"
        >
          <Form.Item
            name="verificationCode"
            label={
              <span>
                <MailOutlined style={{ marginRight: '4px' }} />
                邮箱验证码
              </span>
            }
            rules={[
              { required: true, message: '请输入验证码' },
              { len: 6, message: '验证码为6位数字' }
            ]}
            style={{ marginBottom: '16px' }}
          >
            <Input
              size="large"
              placeholder="请输入6位数字验证码"
              maxLength={6}
              style={{ 
                textAlign: 'center',
                letterSpacing: '2px',
                fontSize: '16px'
              }}
            />
          </Form.Item>

          {/* 重新发送验证码 */}
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ fontSize: '13px', color: '#666', marginBottom: '8px', display: 'block' }}>
              没有收到验证码？请先输入图形验证码，然后重新发送
            </Text>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              marginBottom: '8px'
            }}>
              <div 
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  minWidth: '80px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fff'
                }}
                dangerouslySetInnerHTML={{ __html: resendCaptcha.captchaSvg }}
                onClick={getResendCaptcha}
                title="点击刷新验证码"
              />
              <Form.Item 
                name="resendCaptchaCode" 
                style={{ margin: 0, flex: 1 }}
              >
                <Input 
                  placeholder="图形验证码"
                  maxLength={4}
                  style={{ textTransform: 'uppercase' }}
                />
              </Form.Item>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={getResendCaptcha}
                title="刷新验证码"
              >
                刷新
              </Button>
            </div>
            
            <Button 
              type="dashed"
              onClick={() => handleResendVerification(form.getFieldValue('resendCaptchaCode'))}
              loading={isResending}
              disabled={resendCooldown > 0}
              block
              icon={<MailOutlined />}
            >
              {resendCooldown > 0 ? `${resendCooldown}秒后可重新发送` : '重新发送验证码'}
            </Button>
          </div>

          {/* 提交按钮 */}
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={isVerifying}
              size="large"
              block
              icon={<SafetyOutlined />}
            >
              {isVerifying ? '验证中...' : '验证并登录'}
            </Button>
          </Form.Item>
        </Form>

        {/* 安全提示 */}
        <div style={{ 
          marginTop: '16px',
          padding: '12px',
          background: '#f6ffed',
          borderRadius: '6px',
          border: '1px solid #d9f7be',
          textAlign: 'center'
        }}>
          <Text style={{ 
            fontSize: '12px',
            color: '#52c41a'
          }}>
            🛡️ 为了保护您的账户安全，我们会在检测到异常登录时要求身份验证
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default RiskVerificationModal;
